import React, { useState, useMemo } from 'react';

export interface ExponentialPoint {
  x: number;
  y: number;
  label?: string;
}

export interface ExponentialBuilderProps {
  points: ExponentialPoint[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  xMax?: number;
  yMax?: number;
  // Equation: y = a · b^n
  correctA?: number;
  correctB?: number;
  availableNumbers?: number[];
  selectedA?: number | null;
  selectedB?: number | null;
  onSelectA?: (value: number | null) => void;
  onSelectB?: (value: number | null) => void;
  isSubmitted?: boolean;
  isCorrect?: boolean;
  showPetriDish?: boolean;
  petriDishValue?: number;
  size?: number;
}

const ExponentialBuilder: React.FC<ExponentialBuilderProps> = ({
  points,
  xAxisLabel = 'x',
  yAxisLabel = 'y',
  xMax = 3,
  yMax = 12,
  availableNumbers = [2, 4, 6, 8],
  selectedA,
  selectedB,
  onSelectA,
  onSelectB,
  isSubmitted = false,
  isCorrect,
  showPetriDish = true,
  petriDishValue = 8,
  size = 320,
}) => {
  const [activeSlot, setActiveSlot] = useState<'a' | 'b' | null>(null);

  const padding = { top: 30, right: 30, bottom: 50, left: 60 };
  const chartWidth = size - padding.left - padding.right;
  const chartHeight = size - padding.top - padding.bottom;

  // Convert data coordinates to pixel coordinates
  const toPixel = (x: number, y: number) => ({
    px: padding.left + (x / xMax) * chartWidth,
    py: padding.top + chartHeight - (y / yMax) * chartHeight,
  });

  // Y-axis ticks
  const yTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let i = 0; i <= yMax; i += 2) {
      ticks.push(i);
    }
    return ticks;
  }, [yMax]);

  // X-axis ticks
  const xTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let i = 0; i <= xMax; i++) {
      ticks.push(i);
    }
    return ticks;
  }, [xMax]);

  const handleNumberClick = (num: number) => {
    if (isSubmitted) return;

    if (activeSlot === 'a') {
      onSelectA?.(selectedA === num ? null : num);
    } else if (activeSlot === 'b') {
      onSelectB?.(selectedB === num ? null : num);
    }
  };

  const getSlotStyle = (slot: 'a' | 'b') => {
    const isActive = activeSlot === slot;
    const value = slot === 'a' ? selectedA : selectedB;
    const hasValue = value !== null && value !== undefined;

    let borderColor = '#cbd5e1';
    if (isSubmitted) {
      borderColor = isCorrect ? '#10b981' : '#ef4444';
    } else if (isActive) {
      borderColor = '#818cf8';
    }

    return {
      border: `2px dashed ${borderColor}`,
      background: hasValue ? '#f8fafc' : 'transparent',
      cursor: isSubmitted ? 'default' : 'pointer',
    };
  };

  return (
    <div className="flex flex-col items-center gap-6">
      {/* Chart and Petri Dish row */}
      <div className="flex items-start gap-8">
        {/* Chart */}
        <svg width={size} height={size} className="overflow-visible">
          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={padding.top + chartHeight}
            stroke="#1e293b"
            strokeWidth="2"
          />
          {/* Y-axis arrow */}
          <polygon
            points={`${padding.left},${padding.top - 8} ${padding.left - 5},${padding.top} ${padding.left + 5},${padding.top}`}
            fill="#1e293b"
          />
          {/* Y-axis label */}
          <text
            x={padding.left - 10}
            y={padding.top - 15}
            fontSize="14"
            fontWeight="bold"
            fill="#1e293b"
          >
            {yAxisLabel}
          </text>

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={padding.top + chartHeight}
            x2={padding.left + chartWidth}
            y2={padding.top + chartHeight}
            stroke="#1e293b"
            strokeWidth="2"
          />
          {/* X-axis arrow */}
          <polygon
            points={`${padding.left + chartWidth + 8},${padding.top + chartHeight} ${padding.left + chartWidth},${padding.top + chartHeight - 5} ${padding.left + chartWidth},${padding.top + chartHeight + 5}`}
            fill="#1e293b"
          />
          {/* X-axis label */}
          <text
            x={padding.left + chartWidth / 2}
            y={padding.top + chartHeight + 40}
            fontSize="14"
            fontWeight="bold"
            fill="#1e293b"
            textAnchor="middle"
          >
            {xAxisLabel}
          </text>

          {/* Y-axis ticks and labels */}
          {yTicks.map((tick) => {
            const { py } = toPixel(0, tick);
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={padding.left - 5}
                  y1={py}
                  x2={padding.left}
                  y2={py}
                  stroke="#1e293b"
                  strokeWidth="1"
                />
                <text
                  x={padding.left - 10}
                  y={py + 4}
                  fontSize="12"
                  fill="#64748b"
                  textAnchor="end"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {/* X-axis ticks and labels */}
          {xTicks.map((tick) => {
            const { px } = toPixel(tick, 0);
            return (
              <g key={`x-${tick}`}>
                <line
                  x1={px}
                  y1={padding.top + chartHeight}
                  x2={px}
                  y2={padding.top + chartHeight + 5}
                  stroke="#1e293b"
                  strokeWidth="1"
                />
                <text
                  x={px}
                  y={padding.top + chartHeight + 20}
                  fontSize="12"
                  fill="#64748b"
                  textAnchor="middle"
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {/* Data points */}
          {points.map((point, idx) => {
            const { px, py } = toPixel(point.x, point.y);
            return (
              <g key={idx}>
                <circle
                  cx={px}
                  cy={py}
                  r={6}
                  fill="#14b8a6"
                  stroke="#0f766e"
                  strokeWidth="2"
                />
                {point.label && (
                  <text
                    x={px - 35}
                    y={py - 10}
                    fontSize="12"
                    fill="#1e293b"
                  >
                    {point.label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Petri Dish visualization */}
        {showPetriDish && (
          <div className="flex flex-col items-center gap-2">
            <svg width={100} height={100} viewBox="0 0 100 100">
              {/* Dish outline */}
              <circle cx={50} cy={50} r={45} fill="#f8fafc" stroke="#1e293b" strokeWidth="3" />

              {/* Dots representing items (leaves, bacteria, etc) */}
              {Array.from({ length: 36 }).map((_, i) => {
                const row = Math.floor(i / 6);
                const col = i % 6;
                const x = 20 + col * 12;
                const y = 20 + row * 12;
                const isFilled = i < petriDishValue;

                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r={4}
                    fill={isFilled ? '#14b8a6' : '#cbd5e1'}
                  />
                );
              })}
            </svg>
            <div
              className="px-2 py-1 rounded text-white font-bold text-sm"
              style={{ background: '#1e293b' }}
            >
              {petriDishValue}
            </div>
          </div>
        )}
      </div>

      {/* Equation builder */}
      <div
        className="p-4 rounded-xl w-full max-w-sm"
        style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}
      >
        <div className="flex items-center justify-center gap-2 text-xl font-medium mb-4">
          <span style={{ color: '#64748b' }}>y</span>
          <span style={{ color: '#64748b' }}>=</span>

          {/* Slot A */}
          <button
            className="w-10 h-10 rounded flex items-center justify-center text-lg font-bold transition-all"
            style={getSlotStyle('a')}
            onClick={() => !isSubmitted && setActiveSlot(activeSlot === 'a' ? null : 'a')}
          >
            {selectedA ?? ''}
          </button>

          <span style={{ color: '#64748b' }}>·</span>

          {/* Slot B */}
          <button
            className="w-10 h-10 rounded flex items-center justify-center text-lg font-bold transition-all"
            style={getSlotStyle('b')}
            onClick={() => !isSubmitted && setActiveSlot(activeSlot === 'b' ? null : 'b')}
          >
            {selectedB ?? ''}
          </button>

          <span className="text-sm align-top" style={{ color: '#ec4899' }}>
            <sup>-n</sup>
          </span>
        </div>

        {/* Number options */}
        <div className="flex justify-center gap-2">
          {availableNumbers.map((num) => {
            const isSelectedA = selectedA === num;
            const isSelectedB = selectedB === num;
            const isUsed = isSelectedA || isSelectedB;

            return (
              <button
                key={num}
                className={`w-12 h-12 rounded-lg text-lg font-bold transition-all ${
                  isUsed ? 'opacity-50' : 'hover:bg-slate-200'
                }`}
                style={{
                  background: isUsed ? '#e2e8f0' : '#ffffff',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  cursor: isSubmitted || (!activeSlot && !isUsed) ? 'default' : 'pointer',
                }}
                onClick={() => handleNumberClick(num)}
                disabled={isSubmitted}
              >
                {num}
              </button>
            );
          })}
        </div>

        {/* Active slot indicator */}
        {activeSlot && !isSubmitted && (
          <p className="text-center text-sm mt-2" style={{ color: '#818cf8' }}>
            Select a number for the {activeSlot === 'a' ? 'first' : 'second'} slot
          </p>
        )}
      </div>
    </div>
  );
};

export default ExponentialBuilder;
